import React, { useState, useEffect, useRef } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { motion } from 'framer-motion';

const SitehoundPrizeWheel = ({ prizes = [] }) => {
  const socket = useSocket();
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [winner, setWinner] = useState(null);
  const wheelRef = useRef(null);

  // Sitehound brand colors - using primary color #5A7D91
  const sitehoundColors = [
    '#5A7D91', '#6B8BA3', '#7C9AB5', '#8DA9C7', '#9EB8D9', '#AFC7EB'
  ];

  useEffect(() => {
    if (socket) {
      socket.on('spin_result', (data) => {
        console.log('Sitehound spin result:', data);
        if (data.error) {
          console.error('Spin error:', data.error);
          setIsSpinning(false);
          return;
        }
        
        // Debug: Log prize information
        console.log('Current prizes:', prizes);
        console.log('Prize order:', prizes.map((p, i) => `${i}: ${p.name}`));
        console.log('Winning prize:', data.prize.name);
        
        // Find the winning prize index
        const winningIndex = prizes.findIndex(p => p.name === data.prize.name);
        console.log('Winning prize index:', winningIndex);
        
        if (winningIndex === -1) {
          console.error('Winning prize not found in prizes array!');
          setIsSpinning(false);
          return;
        }
        
        // Start spinning animation when we receive the result
        setIsSpinning(true);
        setWinner(null);
        
        // FRESH START - Simple coordinate system
        // 0° = 3 o'clock (pointer location), 90° = 6 o'clock, 180° = 9 o'clock, 270° = 12 o'clock
        // Positive rotation = clockwise
        
        const segmentAngle = 360 / prizes.length; // 60° for 6 prizes
        
        // MATCH THE VISUAL SEGMENTS: They use (index * segmentAngle) - 90 + (segmentAngle / 2)
        // This puts Sticker Pack center at -30° as you observed
        const segmentStartAngle = (winningIndex * segmentAngle) - 90 + (segmentAngle / 2);
        const segmentCenterAngle = segmentStartAngle + (segmentAngle / 2);
        
        // Calculate where the winning segment currently is (after all previous rotations)
        const currentSegmentPosition = (segmentCenterAngle + (rotation % 360)) % 360;
        
        // We want the segment center to end up at 0° (pointer location)
        // Calculate how much more rotation is needed from current position
        let rotationNeeded = -currentSegmentPosition;
        
        // Ensure we always rotate in the positive direction (clockwise)
        if (rotationNeeded <= 0) {
          rotationNeeded += 360;
        }
        
        // Add exactly 5-8 full rotations (no decimals to avoid rounding errors)
        const fullRotations = (5 + Math.floor(Math.random() * 4)) * 360; // Exactly 5, 6, 7, or 8 full rotations
        
        // ACCUMULATIVE APPROACH: Add to current rotation to ensure full spins
        const totalRotation = rotation + fullRotations + rotationNeeded;
        
        console.log('=== FRESH START DEBUG ===');
        console.log('Current wheel rotation:', rotation);
        console.log(`Prize: ${data.prize.name} (index ${winningIndex})`);
        console.log(`Original segment center: ${segmentCenterAngle}°`);
        console.log(`Current segment position: ${currentSegmentPosition}° (after ${rotation % 360}° rotation)`);
        console.log(`Rotation needed: ${rotationNeeded}° (to bring current position to 0°)`);
        console.log(`Full rotations: ${fullRotations}°`);
        console.log(`Total new rotation: ${totalRotation}°`);
        console.log('');
        console.log('COORDINATE SYSTEM CHECK:');
        console.log('All segments and their centers:');
        for (let i = 0; i < prizes.length; i++) {
          const start = (i * segmentAngle) - 90 + (segmentAngle / 2);
          const center = start + (segmentAngle / 2);
          console.log(`  ${i}: ${prizes[i].name} - start: ${start}°, center: ${center}°`);
        }
        console.log('Expected: Pointer at 0° should point to winning segment center');
        console.log('=== END FRESH START ===');
        
        
        // Immediate debug: Show expected final position
        const expectedFinalRotation = totalRotation % 360;
        console.log('Expected final rotation (mod 360):', expectedFinalRotation);
        
        // Debug: Show where each segment will be after rotation
        console.log('=== EXPECTED FINAL POSITIONS ===');
        prizes.forEach((prize, index) => {
          const segmentStart = (index * segmentAngle) - 90 + (segmentAngle / 2);
          const segmentCenter = segmentStart + (segmentAngle / 2);
          const finalCenter = (segmentCenter + expectedFinalRotation) % 360;
          console.log(`Segment ${index} (${prize.name}): final center at ${finalCenter.toFixed(1)}°`);
        });
        console.log('Pointer is at 0° - should point to winning segment center');
        console.log('=== END EXPECTED POSITIONS ===');
        
        // DIRECT ROTATION: Apply rotation immediately (no reset needed)
        console.log('Applying new rotation:', totalRotation);
        setRotation(totalRotation);
        
        // Show winner after spin animation completes
        setTimeout(() => {
          console.log('=== DOM VERIFICATION DEBUG ===');
          
          // Get the actual wheel element and its rotation
          const wheelElement = wheelRef.current;
          if (wheelElement) {
            const computedStyle = getComputedStyle(wheelElement);
            const transform = computedStyle.transform;
            console.log('Actual CSS transform:', transform);
            
            // Try to extract rotation from transform matrix
            if (transform && transform !== 'none') {
              let actualRotation = 0;
              const matrix = transform.match(/matrix\\(([^)]+)\\)/);
              console.log('Matrix match:', matrix);
              console.log('Transform string:', transform);
              
              if (matrix) {
                const values = matrix[1].split(', ');
                const a = parseFloat(values[0]);
                const b = parseFloat(values[1]);
                actualRotation = Math.atan2(b, a) * (180 / Math.PI);
                console.log('Matrix values: a=', a, 'b=', b);
                console.log('Extracted actual rotation:', actualRotation.toFixed(1), '°');
              } else {
                // Parse the actual transform string manually
                const matrixMatch = transform.match(/matrix\(([^)]+)\)/);
                if (matrixMatch) {
                  const values = matrixMatch[1].split(', ').map(parseFloat);
                  const a = values[0];
                  const b = values[1];
                  actualRotation = Math.atan2(b, a) * (180 / Math.PI);
                  console.log('Parsed from actual transform:', actualRotation.toFixed(1), '°');
                } else {
                  console.log('Could not parse transform matrix');
                  actualRotation = 0;
                }
              }
              
              // Calculate where each segment actually is
              console.log('ACTUAL SEGMENT POSITIONS:');
              prizes.forEach((prize, index) => {
                // Visual segments use: startAngle = (index * segmentAngle) - 90 + (segmentAngle / 2)
                const visualSegmentStart = (index * segmentAngle) - 90 + (segmentAngle / 2);
                const visualSegmentCenter = visualSegmentStart + (segmentAngle / 2);
                const actualFinalCenter = (visualSegmentCenter + actualRotation) % 360;
                
                console.log(`Segment ${index} (${prize.name}): visual center ${visualSegmentCenter}° + rotation ${actualRotation.toFixed(1)}° = ${actualFinalCenter.toFixed(1)}°`);
              });
              
              // Find which segment is closest to 0°
              let closestIndex = -1;
              let closestDistance = 360;
              prizes.forEach((prize, index) => {
                const visualSegmentStart = (index * segmentAngle) - 90 + (segmentAngle / 2);
                const visualSegmentCenter = visualSegmentStart + (segmentAngle / 2);
                const actualFinalCenter = (visualSegmentCenter + actualRotation) % 360;
                const distanceToPointer = Math.min(
                  Math.abs(actualFinalCenter - 0),
                  Math.abs(actualFinalCenter - 360),
                  Math.abs(actualFinalCenter + 360)
                );
                
                if (distanceToPointer < closestDistance) {
                  closestDistance = distanceToPointer;
                  closestIndex = index;
                }
              });
              
              console.log(`POINTER ANALYSIS:`);
              console.log(`- Closest segment to 0°: ${closestIndex} (${prizes[closestIndex]?.name}) at distance ${closestDistance.toFixed(1)}°`);
              console.log(`- Expected winner: ${winningIndex} (${data.prize.name})`);
              console.log(`- Match: ${closestIndex === winningIndex ? 'YES' : 'NO'}`);
              
              // EMPIRICAL FIX: Find which segment is at 330° (11 o'clock)
              let elevenOClockIndex = -1;
              let elevenOClockDistance = 360;
              prizes.forEach((prize, index) => {
                const visualSegmentStart = (index * segmentAngle) - 90 + (segmentAngle / 2);
                const visualSegmentCenter = visualSegmentStart + (segmentAngle / 2);
                const actualFinalCenter = (visualSegmentCenter + actualRotation) % 360;
                const distanceTo330 = Math.min(
                  Math.abs(actualFinalCenter - 330),
                  Math.abs(actualFinalCenter - 330 + 360),
                  Math.abs(actualFinalCenter - 330 - 360)
                );
                
                if (distanceTo330 < elevenOClockDistance) {
                  elevenOClockDistance = distanceTo330;
                  elevenOClockIndex = index;
                }
              });
              
              console.log(`11 O'CLOCK ANALYSIS:`);
              console.log(`- Closest segment to 330° (11 o'clock): ${elevenOClockIndex} (${prizes[elevenOClockIndex]?.name}) at distance ${elevenOClockDistance.toFixed(1)}°`);
              console.log(`- Does this match expected winner? ${elevenOClockIndex === winningIndex ? 'YES' : 'NO'}`);
            }
          }
          
          console.log('=== END DOM VERIFICATION ===');
          
          setWinner(data.prize.name);
          setIsSpinning(false);
        }, 4100); // Slightly after animation completes
      });

      return () => {
        socket.off('spin_result');
      };
    }
  }, [socket, rotation, prizes]);

  const segmentAngle = prizes.length > 0 ? 360 / prizes.length : 60;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #4A6B7F 0%, #5A7D91 50%, #6B8BA3 100%)',
      padding: '2rem'
    }}>

      {/* Prize Wheel */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Wheel Container */}
        <motion.div
          ref={wheelRef}
          animate={{ rotate: rotation }}
          transition={{ 
            duration: isSpinning ? 4 : 0,
            ease: isSpinning ? "easeOut" : "linear"
          }}
          style={{
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            border: '12px solid white',
            boxShadow: '0 0 50px rgba(0,0,0,0.4)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Wheel Segments */}
          {prizes.map((prize, index) => {
            // Rotate segments so they align properly with the right-side pointer
            const startAngle = (index * segmentAngle) - 90 + (segmentAngle / 2);
            const endAngle = startAngle + segmentAngle;
            
            // Debug logging for first render
            if (index === 0) {
              console.log('Segment 0 (first prize):', prize.name);
              console.log('Segment 0 start angle:', startAngle);
              console.log('Segment 0 end angle:', endAngle);
            }
            const color = sitehoundColors[index % sitehoundColors.length];
            
            // Calculate the points for the triangular segment
            const centerX = 50;
            const centerY = 50;
            const radius = 50;
            
            const startRadians = (startAngle * Math.PI) / 180;
            const endRadians = (endAngle * Math.PI) / 180;
            
            const x1 = centerX + radius * Math.cos(startRadians);
            const y1 = centerY + radius * Math.sin(startRadians);
            const x2 = centerX + radius * Math.cos(endRadians);
            const y2 = centerY + radius * Math.sin(endRadians);
            
            const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`;
            
            // Calculate text position (midpoint of the arc)
            const midAngle = startAngle + segmentAngle / 2;
            const textRadius = radius * 0.7; // Position text at 70% of radius
            const textAngle = (midAngle * Math.PI) / 180;
            const textX = centerX + textRadius * Math.cos(textAngle);
            const textY = centerY + textRadius * Math.sin(textAngle);
            
            // All text oriented the same way - no flipping
            const textRotation = midAngle;
            
            return (
              <div key={index} style={{ position: 'absolute', width: '100%', height: '100%' }}>
                <svg
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0
                  }}
                  viewBox="0 0 100 100"
                >
                  <path
                    d={pathData}
                    fill={color}
                    stroke="white"
                    strokeWidth="0.5"
                  />
                  {prize.image ? (
                    <image
                      x={textX - 8}
                      y={textY - 8}
                      width="16"
                      height="16"
                      href={prize.image}
                      transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                      style={{
                        filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))'
                      }}
                    />
                  ) : (
                    <text
                      x={textX}
                      y={textY}
                      fill="white"
                      fontSize="4"
                      fontWeight="bold"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                      style={{
                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                        filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))'
                      }}
                    >
                      {prize.name.toUpperCase()}
                    </text>
                  )}
                </svg>
              </div>
            );
          })}
        </motion.div>

        {/* Pointer */}
        <div style={{
          position: 'absolute',
          top: '50%',
          right: '-15px',
          transform: 'translateY(-50%)',
          width: 0,
          height: 0,
          borderTop: '20px solid transparent',
          borderBottom: '20px solid transparent',
          borderRight: '40px solid #fbbf24',
          filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.4))',
          zIndex: 10
        }} />

        {/* Center Circle */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: 'linear-gradient(45deg, #5A7D91, #4A6B7F)',
          border: '6px solid white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
          zIndex: 10
        }}>
          <img 
            src="/charlie.png" 
            alt="Charlie" 
            style={{ 
              width: '60px', 
              height: '60px',
              borderRadius: '50%',
              objectFit: 'cover'
            }} 
          />
        </div>
      </div>

      {/* Winner Display */}
      {winner && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            padding: '2rem',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            border: '2px solid #fbbf24'
          }}
        >
          <h2 style={{ 
            color: '#5A7D91', 
            margin: '0 0 1rem 0',
            fontSize: '2rem'
          }}>
            🎉 Congratulations! 🎉
          </h2>
          <p style={{ 
            color: '#4A6B7F', 
            fontSize: '1.3rem',
            margin: 0,
            fontWeight: 'bold'
          }}>
            You won: {winner}
          </p>
        </motion.div>
      )}

      {/* Status Display */}
      {isSpinning && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            color: 'white',
            fontSize: '1.5rem',
            fontWeight: 'bold',
            textAlign: 'center',
            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
          }}
        >
          🎲 Spinning the wheel...
        </motion.div>
      )}
    </div>
  );
};

export default SitehoundPrizeWheel;
